<template>
  <div class="group bg-white rounded-[24px] border border-[#1c4848]/5 shadow-sm hover:shadow-[0_20px_40px_-12px_rgba(28,72,72,0.1)] hover:border-[#1c4848]/10 transition-all duration-300 flex flex-col sm:flex-row relative overflow-hidden h-full">
    <div class="absolute top-3 right-3 z-20 flex gap-2">
      <button
        type="button"
        class="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm text-emerald-600 flex items-center justify-center hover:bg-emerald-500 hover:text-white transition-all shadow-sm opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 duration-300"
        aria-label="Publish to feed"
        title="Publish to feed"
        @click.stop="publishToFeed"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
      </button>
      <button
        v-if="book?.id"
        type="button"
        class="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm text-[#1c4848]/70 flex items-center justify-center hover:bg-[#1c4848]/90 hover:text-white transition-all shadow-sm opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 duration-300"
        aria-label="Duplicate book"
        title="Duplicate book"
        :disabled="duplicateLoading"
        @click.stop="openDuplicateConfirm"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
      </button>
      <button
        type="button"
        class="w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm text-[#1c4848] flex items-center justify-center hover:bg-[#1c4848] hover:text-white transition-all shadow-sm opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 duration-300"
        aria-label="Edit"
        @click.stop="$emit('edit', book)"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
      </button>
    </div>

    <div class="w-full sm:w-[200px] shrink-0 relative bg-gray-100 min-h-[240px] sm:min-h-full overflow-hidden group/book">
      <template v-if="coverUrl">
        <img :src="coverUrl" :alt="title" class="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/book:scale-105">
        <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none opacity-50" />
      </template>
      <div v-else class="absolute inset-0 bg-[#1c4848] flex flex-col items-center justify-center p-4">
        <div class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 mix-blend-overlay" />
        <div class="relative z-10 flex flex-col items-center">
          <div class="w-16 h-16 rounded-full bg-[#ecd79f]/10 flex items-center justify-center mb-3 ring-1 ring-[#ecd79f]/20 group-hover:scale-110 transition-transform duration-500">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-[#ecd79f]"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><path d="M8 7h8"/><path d="M8 11h8"/></svg>
          </div>
          <span class="text-[10px] text-[#f2f4f4]/60 text-center font-serif uppercase tracking-widest">No Cover</span>
        </div>
      </div>
    </div>

    <div class="flex-1 flex flex-col p-6 min-w-0">
      <div class="flex flex-wrap gap-2 mb-3">
        <span
          v-for="badge in statusBadges"
          :key="badge.label"
          class="px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider border"
          :class="badge.class"
        >
          {{ badge.label }}
        </span>
      </div>

      <h3 class="font-serif text-xl font-bold text-[#1c4848] leading-tight mb-2 truncate pr-8 group-hover:text-[#00695c] transition-colors tracking-tight">
        <RouterLink v-if="book?.id" :to="`/books/${book.id}`" class="hover:underline">
          {{ title }}
        </RouterLink>
        <span v-else>{{ title }}</span>
      </h3>

      <div class="flex items-center gap-3 text-[10px] text-[#1c4848]/50 font-semibold mb-4 font-mono">
        <span class="flex items-center gap-1 px-1.5 py-0.5 bg-[#1c4848]/5 rounded">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8 15 3"/></svg>
          {{ editionLabel }}
        </span>
        <span class="w-1 h-1 rounded-full bg-[#1c4848]/20" />
        <span>ISBN: {{ isbnDisplay }}</span>
      </div>

      <p class="text-xs text-[#1c4848]/70 line-clamp-2 leading-relaxed font-medium mb-4">
        {{ descriptionDisplay }}
      </p>

      <div v-if="tagList.length" class="flex flex-wrap gap-2 mb-5">
        <span
          v-for="tag in tagList"
          :key="tag"
          class="px-2 py-1 rounded-md bg-[#fafbfc] border border-[#1c4848]/10 text-[10px] font-semibold text-[#1c4848]/60"
        >
          # {{ tag }}
        </span>
      </div>

      <div class="grid grid-cols-3 gap-2 py-3 border-t border-dashed border-[#1c4848]/10 mt-auto bg-[#fafbfc]/50 rounded-xl px-2">
        <div class="flex flex-col items-center sm:items-start">
          <span class="text-[9px] text-[#1c4848]/40 font-bold uppercase tracking-widest">Pages</span>
          <span class="text-sm font-semibold text-[#1c4848] flex items-center gap-1.5 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-[#ecd79f] shrink-0"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/><path d="M10 9H8"/></svg>
            {{ pagesDisplay }}
          </span>
        </div>
        <div class="flex flex-col items-center sm:items-start border-l border-[#1c4848]/5 pl-3">
          <span class="text-[9px] text-[#1c4848]/40 font-bold uppercase tracking-widest">Views</span>
          <span class="text-sm font-semibold text-[#1c4848] flex items-center gap-1.5 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-[#1c4848]/20 shrink-0"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            {{ viewsDisplay }}
          </span>
        </div>
        <div class="flex flex-col items-center sm:items-start border-l border-[#1c4848]/5 pl-3">
          <span class="text-[9px] text-[#1c4848]/40 font-bold uppercase tracking-widest">Rating</span>
          <span class="text-sm font-semibold text-[#1c4848] flex items-center gap-1.5 mt-0.5">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="text-[#f57f17] shrink-0"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
            {{ ratingDisplay }}
          </span>
        </div>
      </div>

      <div class="flex items-center justify-between mt-4 pt-3 border-t border-[#1c4848]/5">
        <span class="text-[10px] font-semibold text-[#1c4848]/30">{{ updatedLabel }}</span>
        <div class="flex gap-2">
          <RouterLink
            v-if="book?.id"
            :to="`/books/${book.id}`"
            class="px-3 py-1.5 rounded-lg bg-[#1c4848] text-white text-[10px] font-semibold shadow-md hover:bg-[#153838] transition-all transform active:scale-95 inline-block"
          >
            Manage Book
          </RouterLink>
          <button
            v-else
            type="button"
            class="px-3 py-1.5 rounded-lg bg-[#1c4848] text-white text-[10px] font-semibold shadow-md hover:bg-[#153838] transition-all transform active:scale-95"
            @click="$emit('edit', book)"
          >
            Edit Draft
          </button>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-model="showDuplicateConfirm"
      title="Kopiraj knjigu"
      :message="duplicateConfirmMessage"
      confirm-label="Kopiraj"
      cancel-label="Odustani"
      @confirm="confirmDuplicate"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useContentFeedStore } from '@/store/content-feed';
import { useBooksStore } from '@/store/books';
import { getContentFeedType, getContentLanguageId } from '@/utils/contentFeedType.js';
import ConfirmDialog from '@/components/app/dashboard/modals/ConfirmDialog.vue';

const props = defineProps({
  title: { type: String, default: '' },
  book: { type: Object, default: null },
});

const emit = defineEmits(['edit', 'delete', 'duplicated']);

const contentFeedStore = useContentFeedStore();
const booksStore = useBooksStore();

const showDuplicateConfirm = ref(false);
const duplicateLoading = ref(false);

const duplicateConfirmMessage = computed(() => {
  const t = title.value;
  return `Da li želite da napravite kopiju knjige „${t}"? Nova knjiga će dobiti naslov sa oznakom (kopija).`;
});

function openDuplicateConfirm() {
  if (!props.book?.id || duplicateLoading.value) return;
  showDuplicateConfirm.value = true;
}

async function confirmDuplicate() {
  const book = props.book;
  if (!book?.id) return;
  duplicateLoading.value = true;
  try {
    await booksStore.duplicateBook(book.id, {});
    showDuplicateConfirm.value = false;
    emit('duplicated', book);
  } catch (e) {
    console.error(e);
  } finally {
    duplicateLoading.value = false;
  }
}

function publishToFeed() {
  const book = props.book;
  if (!book?.id) return;
  if (!confirm('Publish this book to feed?')) return;
  const feedTitle = book?.name ?? book?.title ?? 'Book';
  const type = getContentFeedType(book, 'book');
  const languageId = getContentLanguageId(book);
  contentFeedStore.createContentFeed({ title: feedTitle, type, language_id: languageId }).then((res) => {
    booksStore.attachContentFeedToBook(book.id, res.id);
  });
}

const coverUrl = computed(() => {
  const b = props.book;
  if (!b) return '';
  return b.coverImage?.url ?? b.cover_image?.url ?? (typeof b.cover_image === 'string' ? b.cover_image : '');
});

const title = computed(() => props.book?.name || props.title || 'Untitled');

const editionLabel = computed(() => props.book?.edition || 'Standard');

const isbnDisplay = computed(() => props.book?.isbn || '—');

const descriptionDisplay = computed(() => props.book?.description || 'No description.');

const tagList = computed(() => {
  const t = props.book?.tags;
  if (Array.isArray(t)) return t.map((x) => (typeof x === 'object' ? x?.name : x)).filter(Boolean);
  if (typeof t === 'string') return t.split(/[,\s]+/).filter(Boolean);
  return [];
});

const statusBadges = computed(() => {
  const b = props.book;
  const badges = [];
  const isPremium = b?.isPremium ?? b?.is_premium ?? false;
  if (isPremium) {
    badges.push({ label: 'Premium', class: 'bg-[#e3f2fd] text-[#1565c0] border-[#1565c0]/10' });
  }
  const status = (b?.status ?? '').toString().toUpperCase();
  if (status) {
    const statusMap = {
      ACTIVE: { label: 'Active', class: 'bg-[#e1f5e8] text-[#00695c] border-[#00695c]/10' },
      IN_PROGRESS: { label: 'In progress', class: 'bg-[#fff8e1] text-[#f57f17] border-[#f57f17]/10' },
      TESTING: { label: 'Test phones only', class: 'bg-[#e8f3f4] text-[#1c4848] border-[#1c4848]/10' },
      INACTIVE: { label: 'Inactive', class: 'bg-gray-100 text-gray-500 border-gray-200' },
    };
    const mapped = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-600 border-gray-200' };
    badges.push(mapped);
  }
  const approval = (b?.approvalStatus ?? b?.approval_status ?? '').toString().toUpperCase();
  if (approval) {
    const approvalMap = {
      APPROVED: { label: 'Approved', class: 'bg-[#e1f5e8] text-[#00695c] border-[#00695c]/10' },
      PENDING: { label: 'Pending', class: 'bg-[#fff8e1] text-[#f57f17] border-[#f57f17]/10' },
      DISAPPROVED: { label: 'Disapproved', class: 'bg-[#ffebee] text-[#c62828] border-[#c62828]/10' },
    };
    const mapped = approvalMap[approval] || { label: approval, class: 'bg-gray-100 text-gray-600 border-gray-200' };
    badges.push(mapped);
  }
  if (!badges.length) {
    badges.push({ label: 'Draft', class: 'bg-gray-100 text-gray-500 border-gray-200' });
  }
  return badges;
});

const pagesDisplay = computed(() => {
  const b = props.book;
  const v = b?.totalPages ?? b?.total_pages ?? b?.pages_count ?? b?.pages;
  return v != null ? Number(v) : 0;
});

const viewsDisplay = computed(() => {
  const b = props.book;
  const v = b?.viewCount ?? b?.view_count ?? b?.views_count ?? b?.views;
  if (v == null) return '0';
  const n = Number(v);
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
});

const ratingDisplay = computed(() => props.book?.rating != null ? Number(props.book.rating).toFixed(1) : '—');

const updatedLabel = computed(() => props.book?.updatedAt ?? props.book?.updated_at ?? '');
</script>
